/**
 * 제출 후 흐름 — TBM 배포 설정 화면(`/reports/:id/tbm-distribution-setup`).
 *
 * 정본: `docs/decisions/2026-09-06-tbm-post-submit-distribution-and-delegation.md` §4·§5.
 * 서버 정본은 `apps/server/src/reports/tbm-distribution-setup.service.ts` — **게이트 판정도
 * 담당자 추천도 서버가 한다.** 이 화면은 판정을 다시 하지 않고 blocker 코드에 안내 문구만
 * 붙이며, 입력을 모아 한 번 POST한다.
 *
 * ## 왜 "설정 저장"이 없는가
 * §4 확정: **설정+발송은 한 액션**이다(구 마법사 동형 — "설정만 저장" 중간 상태가 없다).
 * 그래서 dirty 게이트(2026-08-16 표준)를 걸지 않는다 — 저장 버튼이 지키는 "저장된 값"이라는
 * 것이 아직 없고, 이탈해도 잃는 것은 이번 입력뿐이다. 설정 완료 후 **재진입(수정)** 흐름은
 * 별개이며 그 화면이 생길 때 dirty 표준을 적용한다(W4c 몫).
 *
 * ## 묶음 단위 1회 진입
 * `:id`는 묶음의 아무 문서나 가리킬 수 있고(앵커), 서버가 형제 집합을 돌려준다. **어느 id로
 * 들어와도 화면 내용이 같다** — 그래서 형제 탭(칩)을 두지 않는다(2026-09-07 「배포 설정 화면
 * 단일화」): 탭은 같은 화면을 다른 id로 다시 그릴 뿐이면서 "탭마다 다른 내용"이라는 거짓
 * 기대를 만들었다. 묶음 구성·진행 상태·미완 문서로 가는 링크는 「배포 대상 문서」 목록이 다
 * 맡는다.
 *
 * 입력은 **전부 건별**이다(담당자·교육 기간·리마인더·서명 방식). 서명 방식의 공통 기본값
 * 라디오(구 W7 2층)는 폐기했다 — "공통 따름"은 값이 아니라 참조인데 값처럼 보이는 선택지라
 * 유효 방식을 알려면 위로 올라가 라디오를 봐야 했고, 되돌릴 수 없는 확정임에도 라디오 하나가
 * 여러 건의 유효 방식을 한꺼번에 간접으로 바꿨다. 이제 기본은 전자서명 고정이고, 예외는 행의
 * 「출력물에 직접 서명」 체크로만 생긴다(유효 OFFLINE 건은 발송 대신 서명란 추가 출력 장수).
 *
 * ## 공종별 기억의 복원 (2026-09-07)
 * 서버가 그룹 라벨 축으로 **마지막에 확정한 설정**(서명 방식·발송 채널·리마인더 시간)을
 * 기억해 `rememberedSettings`로 내려준다. 이 화면은 그것을 기본값으로 되살리되 순서가 있다:
 * **저장값 > 기억 > 기본값.** 기억된 담당자를 지금 지정할 수 없으면(비활성·탈퇴·연락 수단
 * 없음, 판정은 서버 술어) 담당자·채널만 비우고 그 사실을 행에 알린다 — 리마인더·서명 방식은
 * 담당자와 무관한 축이라 그대로 복원한다.
 *
 * ## 부분 확정 묶음 (2026-09-08)
 * 그룹 하나를 지우고 다시 만들면 재생성 문서가 같은 배치를 물려받아 **일부만 확정된 묶음**이
 * 된다. 설정 대상은 그때 「묶음 전체」가 아니라 **미확정 문서**다 — 잠금을 `!gateReady` 한
 * 덩어리에서 **문서별**로 내리고(확정된 행만 잠긴다), payload·제출 게이트·버튼 라벨은
 * `pendingDocs`만 본다. 확정된 형제는 **숨기지 않고** 값을 보인 채 잠근다(숨기면 "묶음이
 * 이것뿐"이라는 오해를 만든다) — 그 값의 정본은 서버가 주는 `confirmedEducator`·건별 저장값이다.
 *
 * ## 「내가 직접 진행」 (2026-09-08)
 * 정본: `docs/decisions/2026-09-08-tbm-self-conducted-education.md`. 이 흐름은 담당자 지정을
 * 강제해 **안전관리자가 자기 교육을 여는 입구가 0건**이었다. 건별 토글이 그 갈래를 낸다 —
 * 담당자 지정과 **배타**이고, 켜면 담당자·발송 채널이 접히며 교육 기간·서명 방식은 남는다.
 *
 * 화면이 보내는 것은 **`selfConducted: true` 플래그 하나뿐**이다. 연락처를 실어 보내지 않는
 * 것이 이 갈래의 보안 축이다 — 클라이언트가 `{name, phone}`을 정할 수 있으면 "본인"이라
 * 주장하며 임의 번호로 문자를 보낼 자리가 생긴다. 신원은 서버가 인증된 요청자에서 해소한다.
 *
 * 그래서 이 갈래는 **연락처를 요구하지 않는다** — 토큰이 발급되지 않아 발송이 아예 없고
 * (링크·SMS 0건), 알림은 교육 시각의 작성자 push다. 본인 전화번호가 없는 계정도 막히지 않는다.
 * 발송·연락처·채널을 재는 제출 게이트가 이 행을 세면 **고칠 방법이 없는 잠금**이 되므로
 * (`dispatchDocs`), 그 집합에서 뺀다.
 *
 * ## 문구 규율
 * 그룹 라벨은 **인용만** 한다 — "공종"처럼 이름 붙이지 않는다(분할 축은 양식이 정한다).
 * 안내는 **실제로 일어나는 것만** 약속한다 — 직접 진행 건에 "담당자 링크는 …까지 유효합니다"를
 * 그대로 두면 없는 링크를 약속하는 셈이라, 교육 기간 안내와 완료 토스트도 갈라 적는다.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, DateInput, formatDateDisplay } from '@safetyhub/ui';
import {
  findPostSubmitFlow,
  TBM_OFFLINE_SHEET_COUNT_MAX,
  TBM_OFFLINE_SHEET_COUNT_MIN,
  type TbmSignatureMode,
} from '@safetyhub/shared';
import {
  applyTbmDistributionSetup,
  getDocStatusBadgeTone,
  getReport,
  getTbmDistributionSetup,
  type TbmDistributionSetupData,
  type TbmSetupBlockerCode,
  type TbmSetupDocument,
  type TbmSetupEducator,
} from '../../../lib/reports';
import { resolveReportStatusLabel } from '../../../lib/reportStatusLabel';
import { canConfigureEducationPeriod } from '../../../lib/educationPeriodEligibility';
import { apiErrorMessage, extractApiError } from '../../../lib/api';
import { useAuthStore } from '../../../stores/auth';
import { useToastStore } from '../../../stores/toast';
import { composedEditHref } from '../siblingWriteHref';
import {
  readDetailEntryState,
  resolveDetailBackNav,
  resolveDetailEntryOrigin,
  type DetailBackNav,
} from '../../../components/reports/manifest/detailBackNav';
import { isPostSubmitSetupPath } from '../postSubmitFlows';

/**
 * 진입 state가 없을 때의 back-link(직접 URL·새로고침) — 이 화면은 상세·작성 화면 어느 쪽에서도
 * 들어오므로 「어디서 왔는지 모르면 목록」이 유일하게 안전한 답이다.
 */
const SETUP_BACK_NAV_FALLBACK: DetailBackNav = {
  backLink: '/reports',
  backLabel: '보고서 목록',
};

/** 발송 채널 — 서버 DTO(`channels`)와 같은 어휘. */
type Channel = 'sms' | 'email';

/**
 * 건별 입력 상태. 서버 응답의 저장값·추천값을 초기값으로 받고, 사용자가 만진 것만 덮는다.
 * `educationStartDate`/`EndDate`는 **사용자가 고르기 전까지 undefined**다 — 구 동형으로
 * "지정하지 않으면 다회차 아님"이라, 초기값을 오늘로 채우면 모든 건이 다회차가 된다.
 */
interface DocRowState {
  contactId: string;
  channels: Channel[];
  educationStartDate?: string;
  educationEndDate?: string;
  reminderTime: string;
  /**
   * 「출력물에 직접 서명」 체크 여부 = 이 문서의 유효 서명 방식(체크 OFFLINE / 미체크
   * ELECTRONIC). 3값 select(공통 따름/전자서명/출력물)를 접은 자리라 boolean이 곧 정본이다 —
   * "공통 따름"이라는 **참조 값**이 사라져 유효 방식이 행 하나로 읽힌다.
   */
  offlineSignature: boolean;
  /** 유효 OFFLINE 건의 서명란 추가 출력 장수(구 TBM 동형). */
  offlineSheetCount: number;
  /**
   * 「내가 직접 진행」 — 담당자 지정의 **배타 갈래**(결정 2026-09-08). 켜면 담당자·발송 채널이
   * 사라지고 payload에는 `selfConducted: true` 플래그 하나만 실린다.
   *
   * 담당자 select의 값(`contactId`)과 **함께 지우지 않는다** — 채널이 그렇듯 되돌렸을 때
   * 고르던 값이 살아 있어야 한다. 유효 여부는 이 boolean이 결정하고, 배타는 렌더·payload
   * 조립부가 지킨다(값 두 벌을 동기화하지 않는다).
   */
  selfConducted: boolean;
}

const DEFAULT_REMINDER_TIME = '09:00';

/** 발송 수단을 여는 것은 연락처(SMS)와 이메일(EMAIL)이다 — 없는 수단은 켤 수 없다. */
type Contactable = { phone: string | null; email: string | null };

/**
 * 이 담당자가 **실제로 받을 수 있는** 채널인가. 채널의 어휘와 그 채널을 여는 필드의 대응을
 * 한 자리에 둔다 — 초기화·전환·기본값이 각자 조건을 적으면 어느 쪽이 정본인지 갈린다.
 */
function canReceive(contact: Contactable, channel: Channel): boolean {
  return channel === 'email' ? Boolean(contact.email) : Boolean(contact.phone);
}

/**
 * 담당자에 종속된 기본 발송 채널. **담당자가 없으면 채널도 없고, 받을 수단이 없는 채널은
 * 붙지 않는다** — 받을 수 없는데 발송 채널만 켜져 있는 상태는 화면이 스스로 모순을 그리는
 * 것이고, 사용자는 그것을 "설정됐다"로 읽는다(검수 적발: 추천 담당자가 없는 행에도 SMS가
 * 체크돼 있었다. 연락처 없는 담당자에게 SMS가 붙는 것도 같은 성질이다).
 *
 * 초기화(서버 추천값)와 담당자 select의 "빈 값 ↔ 선택" 전이가 **이 한 함수만** 쓴다. 규칙을
 * 두 벌로 두면 어느 쪽이 정본인지가 화면에서 갈린다(추천으로 들어온 행과 손으로 고른 행의
 * 기본값이 달라지는 식으로).
 */
function defaultChannelsFor(contact: Contactable | null | undefined): Channel[] {
  if (!contact) return [];
  return (['sms', 'email'] as Channel[]).filter((c) => canReceive(contact, c));
}

/**
 * blocker 코드별 안내. 판정은 서버가 끝냈으므로 여기서는 **사유별 문구와 출구**만 정한다.
 * `count`는 서버가 준 `reportIds` 길이 그대로다(화면이 다시 세지 않는다 — 형제 칩 목록과
 * 게이트 집합은 조회 술어가 달라 갈릴 수 있고, 숫자의 정본은 게이트 쪽이다).
 */
function blockerNotice(
  code: TbmSetupBlockerCode,
  count: number,
): { title: string; body: string } {
  switch (code) {
    case 'NOT_ALL_DRAFT':
      return {
        title: `아직 작성이 끝나지 않은 문서가 ${count}건 있습니다`,
        body: '위 문서 목록에서 해당 문서를 눌러 작성을 이어가세요. 묶음의 문서를 모두 완료하면 배포 설정을 진행할 수 있습니다.',
      };
    case 'DELEGATION_IN_PROGRESS':
      return {
        title: `담당자가 작성 중인 문서가 ${count}건 있습니다`,
        body: '위임한 담당자의 작성이 끝나면 배포 설정을 진행할 수 있습니다.',
      };
    case 'APPROVAL_LOCKED':
      return {
        title: '결재가 진행 중입니다',
        body: '결재 중인 문서는 배포 설정을 변경할 수 없습니다. 배포 현황은 문서 상세에서 확인하세요.',
      };
    case 'ALREADY_CONFIGURED':
      return {
        title: '이미 배포 설정이 완료되었습니다',
        body: '서명 방식은 한 번 확정하면 되돌릴 수 없습니다. 배포 현황은 문서 상세에서 확인하세요.',
      };
    case 'POST_SUBMIT_NOT_DECLARED':
      return {
        title: '이 양식은 배포 설정 흐름을 사용하지 않습니다',
        body: '작성한 문서는 저장되어 있습니다. 문서 상세로 이동해 이어서 진행하세요.',
      };
  }
}

/**
 * 날짜를 아직 안 골랐을 때 그 자리에 들어가는 말(구 TBM 동형).
 *
 * 공용 `DateInput`의 기본 placeholder는 「날짜를 선택하세요」지만 이 화면은 두 칸이 한 줄에
 * 나란히 서는 좁은 자리라 짧은 문구를 쓴다 — 짝 spec이 `toHaveTextContent('날짜 선택')`으로
 * 그 상태를 잡으므로, 기본값으로 두면 그 케이스가 red가 된다(그래서 `placeholder`를 명시한다).
 *
 * ⛔ 이 값을 **안내 문구 쪽에 다시 끌어오지 말 것.** 종전에는 로컬 `formatDisplay`가 빈 값에
 * 이 라벨을 돌려줘 「두 자리가 한 값을 나눠 쓴다」고 적혀 있었는데, 그 갈래는 **발동하지
 * 않았다**: 그 함수의 유일한 호출부가 `{isSameDay && …}` 안이고
 * `isSameDay = !!startDate && !!endDate && startDate === endDate`라 **빈 값이 거기 도달할 수
 * 없다**. 그래서 2026-10-27에 그 함수를 공용 `formatDateDisplay`로 걷었고, 이 라벨의 실제
 * 소비처는 아래 두 `DateInput`의 `placeholder` **하나**다.
 */
const EMPTY_DATE_LABEL = '날짜 선택';

/**
 * `YYYY-MM-DD`의 **다음 날**을 화면 표기로. 담당자 링크 만료일(종료일 + 1일) 안내 전용.
 *
 * 표시 문구는 공용 `formatDateDisplay`(`@safetyhub/ui`)가 소유한다 — 이 함수가 가진 것은
 * **+1일 산술**뿐이다. ⛔ 여기서 `toLocaleDateString`으로 직접 포맷하지 말 것: 그러면 이 파일이
 * 「YYYY년 M월 D일」 사본을 다시 소유하게 되고, 같은 줄의 시작일(공용으로 포맷한다)과 문구가
 * 조용히 갈릴 수 있다(값이 같아 **red가 없다**).
 *
 * `Date`를 거치는 것은 날짜 **덧셈** 때문이고, 로컬 자정(`T00:00:00`)으로 파싱해야 한다 —
 * `new Date('YYYY-MM-DD')`는 UTC 자정이라 KST에서 하루 밀린다. 그 `Date`를 다시
 * `YYYY-MM-DD`(로컬 연·월·일)로 되돌려 공용에 넘기므로 월말·연말 경계도 그대로 넘어간다.
 *
 * ## 왜 +1일 산술까지 공용으로 옮기지 않았나
 * 공용은 **표시 포맷**만 소유한다(순수 문자열 — `Date`를 거치지 않는 것이 그 함수의 성질이고
 * 시간대 오프셋 오류가 구조적으로 없는 근거다). 거기에 날짜 **산술**을 넣으면 그 성질이 깨지고,
 * 「+1일」은 포맷 규약이 아니라 **이 화면의 도메인 규칙**이다 — 서버 `calcEducatorTokenExpiresAt`
 * (종료일 + 1일)을 그대로 옮긴 것이라, 그 규칙이 바뀌면 **여기와 서버가 함께** 바뀌어야 한다.
 * 공용에 두면 그 짝이 멀어진다. ⇒ 옮긴 선은 **포맷까지**이고 산술은 호출부 몫이다.
 */
function formatDisplayNextDay(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return formatDateDisplay(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
}

/**
 * 교육 기간(다회차) 입력. 노출 판정은 호출부(S1 게이트)가 한다.
 *
 * 날짜 두 칸은 공용 `DateInput`(`@safetyhub/ui`)이다 — 보이는 것은 버튼, 값의 정본은 `sr-only`
 * date input이고 버튼이 `showPicker()`를 연다. 그 규약(숨김은 반드시 clip이어야 한다 ·
 * `tabIndex={-1}` 유지 · `showPicker` try/catch)과 **왜 bare 입력이 아닌가**는 이제 그
 * 컴포넌트가 소유한다. ⛔ **이 자리에 그 사본을 다시 만들지 말 것** — 종전엔 같은 패턴이
 * web 6파일에 복제돼 있었고 그래서 admin 8자리에는 아예 적용되지 않았다.
 *
 * 접근명도 그 컴포넌트가 `label` 하나에서 파생시킨다 — 입력은 「교육 시작일」/「교육 종료일」,
 * 버튼은 「… 선택」. 둘을 갈라 둔 규약이 **여기서 나왔고**(spec이 그 두 이름으로 칸과 버튼을
 * 각각 잡는다) 공용화가 그것을 손이 아니라 코드로 지키게 한 것이다.
 *
 * ⛔ `placeholder`를 **명시한다** — 공용 기본값은 「날짜를 선택하세요」인데 이 좁은 두 칸은
 * `EMPTY_DATE_LABEL`(「날짜 선택」)을 쓰고 spec이 그 문구로 미선택 상태를 잡는다.
 *
 * ## 세 상태를 말한다 (결정 2026-09-07 확정 4)
 * 종전 화면은 **고르는 방법**만 있고 고르지 않으면 무슨 일이 생기는지를 말하지 않았고,
 * 같은 날을 골랐을 때는 아무 안내도 없었다(`days > 0`이 false라 문구 블록 전체가 빠졌다).
 * 이제 비었을 때 / 같은 날 / 여러 날 각각의 **뜻과 링크 만료**를 한 줄로 말한다.
 *
 * 만료 문구는 서버 `calcEducatorTokenExpiresAt`(종료일 + 1일, 없으면 오늘 + 1일)이 실제로
 * 그렇게 동작한다는 사실만 옮긴 것이다 — 확인되지 않은 약속은 쓰지 않는다.
 *
 * ## 비우는 길
 * 종전엔 한 번 고르면 되돌릴 수 없었다(빈 값 `onChange`를 입력이 무시한다). 「지정 안 함」이
 * 두 날짜를 **함께** 비운다 — 한쪽만 남기면 화면·DTO가 4겹으로 막아 둔 도달 불가 상태를
 * 우리 손으로 만드는 셈이다.
 */
/**
 * 교육 진행 담당자 select — **두 서명 방식이 공유한다**(결정 2026-09-08).
 *
 * 종전엔 이 칸이 전자서명 분기 안에만 있었고, 「출력물에 직접 서명」을 켜면 담당자 지정이
 * 통째로 사라졌다. 그런데 축은 둘이다: *누가 진행하는가*(오프라인에도 있다 — 종이로 하든
 * 누군가 진행한다)와 *어떻게 통지하는가*(오프라인엔 없다). 사라져야 하는 건 뒤엣것뿐이다.
 *
 * 그래서 칸은 한 벌로 두고 **분기는 바깥이 소유한다** — 채널 기본값 세우기(전자서명)나 안내
 * 문구처럼 방식마다 다른 것은 `onChange`·`children`으로 들어온다. 두 벌로 복제하면 후보
 * 목록·추천 배지·접근명 규칙이 갈린다.
 */
function EducatorSelect({
  label,
  candidates,
  recommendedSource,
  value,
  disabled,
  onChange,
  children,
}: {
  /** 그룹 라벨 — 접근명(`{label} 교육 진행 담당자`)에 들어가 여러 행이 이름으로 구별된다. */
  label: string;
  candidates: TbmSetupEducator[];
  /** 서버 추천의 출처 — 없으면 배지 생략. */
  recommendedSource?: TbmSetupEducator['source'];
  value: string;
  disabled: boolean;
  onChange: (contactId: string) => void;
  /** 칸 아래 안내(방식별로 다르다 — 전자서명은 연락처 경고, 오프라인은 인쇄 안내). */
  children?: ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-ink-500 mb-1">
        담당자
        {recommendedSource && (
          <span className="ml-1.5 text-[11px] text-brand-600">
            {recommendedSource === 'delegate' ? '(작성 담당자 추천)' : '(이전 지정 이력 추천)'}
          </span>
        )}
      </label>
      <select
        aria-label={`${label} 교육 진행 담당자`}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-ink-100"
      >
        <option value="">담당자 선택</option>
        {candidates.map((c) => (
          <option key={c.contactId} value={c.contactId}>
            {[c.name, c.department, c.position].filter(Boolean).join(' · ')}
          </option>
        ))}
      </select>
      {children}
    </div>
  );
}

function EducationPeriodFields({
  startDate,
  endDate,
  reminderTime,
  selfConducted,
  disabled,
  onChange,
  onReminderTimeChange,
}: {
  startDate?: string;
  endDate?: string;
  reminderTime: string;
  /**
   * 작성자 직접 진행 건인가 — **날짜 축은 그대로 두고 안내 문장만 가른다**(결정 2026-09-08).
   * 이 갈래엔 담당자 토큰이 발급되지 않으므로(회귀계약 2) "담당자 링크는 …까지 유효합니다"·
   * "담당자에게 알림을 보냅니다"가 전부 거짓이 된다. 이 파일의 규율대로 **확인되지 않은
   * 약속은 쓰지 않는다** — 실제로 일어나는 것(교육 시각의 작성자 push, §5)만 적는다.
   */
  selfConducted: boolean;
  disabled: boolean;
  onChange: (start: string, end: string) => void;
  onReminderTimeChange: (time: string) => void;
}) {
  const days =
    startDate && endDate && startDate !== endDate
      ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000) + 1
      : 0;

  // 3상태 — 판정 축은 서버·상세와 같은 공유 술어(`isMultiSessionEducation`)와 한 뜻이다.
  // 여기 값은 `YYYY-MM-DD` 문자열이라 문자열 비교로 충분하다(같은 규약의 상대 비교).
  const hasAnyDate = !!startDate || !!endDate;
  const isSameDay = !!startDate && !!endDate && startDate === endDate;


  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-ink-500">교육 기간</span>
        {hasAnyDate && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange('', '')}
            data-testid="education-period-clear"
            className="text-xs text-ink-500 underline hover:text-ink-700 disabled:text-ink-300 disabled:no-underline disabled:cursor-not-allowed"
          >
            지정 안 함
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-ink-500 mb-1">교육 시작일</label>
          <DateInput
            label="교육 시작일"
            placeholder={EMPTY_DATE_LABEL}
            size="sm"
            className="w-full"
            value={startDate ?? ''}
            disabled={disabled}
            onChange={(nextStart) => {
              if (!nextStart) return;
              const nextEnd = !endDate || endDate < nextStart ? nextStart : endDate;
              onChange(nextStart, nextEnd);
            }}
          />
        </div>
        <div>
          <label className="block text-xs text-ink-500 mb-1">교육 종료일</label>
          <DateInput
            label="교육 종료일"
            placeholder={EMPTY_DATE_LABEL}
            size="sm"
            className="w-full"
            value={endDate ?? ''}
            min={startDate}
            disabled={disabled || !startDate}
            onChange={(next) => {
              if (!startDate || !next) return;
              onChange(startDate, next);
            }}
          />
        </div>
      </div>
      {!hasAnyDate && (
        <p className="text-xs text-ink-500" data-testid="education-period-hint-unset">
          교육 기간을 지정하지 않으면 지금 바로 진행하는 1회 교육입니다.
          {selfConducted ? '' : ' 담당자 링크는 내일까지 유효합니다.'}
        </p>
      )}
      {isSameDay && (
        <p className="text-xs text-brand-600" data-testid="education-period-hint-same-day">
          {formatDateDisplay(startDate!)} 하루 진행하는 1회 교육입니다.
          {selfConducted ? '' : ` 담당자 링크는 ${formatDisplayNextDay(startDate!)}까지 유효합니다.`}
        </p>
      )}
      {days > 0 && (
        <>
          <p className="text-xs text-brand-600" data-testid="education-period-hint-multi">
            {days}일간 다회 교육입니다.
            {selfConducted
              ? ''
              : ` 담당자 링크는 ${formatDisplayNextDay(endDate!)}까지 유효합니다.`}
          </p>
          <div>
            <label className="block text-xs text-ink-500 mb-1">리마인더 알림 시간</label>
            <input
              type="time"
              aria-label="리마인더 알림 시간"
              value={reminderTime}
              disabled={disabled}
              onChange={(e) => onReminderTimeChange(e.target.value)}
              className="px-2 py-1.5 text-sm border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-ink-100"
            />
            <p className="text-xs text-ink-400 mt-1" data-testid="education-reminder-hint">
              {selfConducted
                ? // 1차 리마인더다 — 에스컬레이션이 아니다(§「알림 시각」). 담당자가 따로 없어
                  // 1시간 유예 없이 이 시각에 바로 온다. 그 사실 그대로만 적는다.
                  '교육 미진행 시 이 시간에 앱 알림을 받습니다.'
                : '교육 미진행 시 담당자에게 알림을 보냅니다.'}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default function TbmDistributionSetupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, currentMembership } = useAuthStore();
  const showToast = useToastStore((s) => s.showToast);
  const companyId = currentMembership?.companyId;
  const flow = findPostSubmitFlow('tbm-distribution');

  /**
   * S1(다회차 = 교육 기간 지정) 입력을 띄울지. **판정 정본은 서버**(POST가 같은 두 축으로
   * 403을 던진다) — 여기서는 어차피 거부될 칸을 감출 뿐이라, 술어의 doc이 그 사실을 소유한다.
   * 서버 조회 응답이 가용 여부를 내리지 않아 멤버십에서 파생하는 것이 현재 형상이다.
   */
  const showEducationPeriod = canConfigureEducationPeriod(currentMembership);

  /**
   * 진입 state의 두 축(F-52) — **「←」가 갈 곳**과 **확정하고 나갈 곳**은 다르다.
   *
   * 상세에서 들어왔으면 「←」는 그 상세로 돌아가고(진입점), 확정 뒤에는 상세가 애초에 넘겨준
   * 원점(목록·점검 상세)으로 나간다. 작성 화면에서 직행했으면 원점이 없어 둘이 같다.
   * 종전에는 「←」가 `/reports` 하드코딩이라, 상세에서 들어와도 목록으로 새어 나갔다(9/13).
   *
   * ⚠️ 진입점이 **이 흐름의 설정 화면**(자기 자신 또는 형제 앵커의 것)이면 폴백으로 떨어뜨린다
   * (리뷰 W6, 2026-09-14). 검수 링크가 `from`에 이 화면을 싣고, 편집 → 저장 → 상세 → §5
   * 라우팅이 다시 설정으로 `replace`하는 사이 그 `from`이 그대로 여기까지 흘러오면 「←」가
   * 자기 화면을 가리켜 아무 데도 못 나간다. 상세의 F-52 가드(`isPostSubmitSetupPath`)와 같은
   * 술어를 쓴다. 원점도 같은 축이다 — 원점을 따로 받지 않았는데 진입점이 설정 화면이면
   * 확정 뒤 이동에 그 경로를 물려주지 않는다(빈 원점 → 목적지 상세의 기본값).
   */
  const rawEntry = readDetailEntryState(location.state);
  const entryFromIsSetupPath = isPostSubmitSetupPath(rawEntry?.from);
  const backNav = entryFromIsSetupPath
    ? SETUP_BACK_NAV_FALLBACK
    : resolveDetailBackNav(location.state, SETUP_BACK_NAV_FALLBACK);
  const entryOrigin =
    entryFromIsSetupPath && !rawEntry?.origin ? {} : resolveDetailEntryOrigin(location.state);

  const {
    data: setup,
    isLoading,
    error: loadError,
  } = useQuery<TbmDistributionSetupData>({
    queryKey: ['tbmDistributionSetup', id, companyId],
    queryFn: () => getTbmDistributionSetup(id!, companyId!),
    enabled: Boolean(id && companyId),
    // 캐시 키는 **문서 하나**(id)인데 응답 내용은 **묶음 전체**(형제 목록 + 게이트 판정)라,
    // 형제 하나의 상태가 바뀌면 나머지 형제의 캐시가 통째로 낡는다. 실효 기본 staleTime은
    // 5분이고(App.tsx 중첩 QueryClientProvider — main.tsx의 0이 아니다) `refetchOnMount: true`는
    // "stale일 때만"이라, 낡은 목록이 그대로 첫 페인트가 됐다(검수에서 DB는 둘 다 DRAFT인데
    // 화면만 「초안+생성중」으로 갈렸다). 게이트 판정 화면이라 **타인의 변경**(다른 사람이
    // 먼저 완료·상신)에도 원리적으로 노출되므로 신선도 자체를 고친다.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  /**
   * 앵커 문서 원본 — 「배포 대상 문서」의 검수 링크가 편집 라우트를 조립할 `templateKey`를
   * 여기서 얻는다(형제는 같은 양식이라 앵커 값이 곧 전 건의 값이다). 상세와 같은 캐시 키라
   * 왕복이 늘지 않는다.
   */
  const { data: anchorReport } = useQuery({
    queryKey: ['report', id],
    queryFn: () => getReport(id!, companyId!),
    enabled: Boolean(id && companyId),
  });

  const [rows, setRows] = useState<Record<string, DocRowState>>({});
  const [formError, setFormError] = useState<string | null>(null);
  /** 발송 거부의 서버 사유 코드 — 문구는 서버 것을 쓰고, 코드는 표시 축(테스트·분기)으로만 둔다. */
  const [blockedBy, setBlockedBy] = useState<TbmSetupBlockerCode | null>(null);

  const candidateById = useMemo(() => {
    const map = new Map<string, TbmSetupEducator>();
    for (const c of setup?.candidates ?? []) map.set(c.contactId, c);
    // 추천 담당자가 후보 목록(활성 현장 담당자)에 없을 수 있다(비활성화·타 현장 이전).
    // select가 그 값을 못 그리면 "추천이 있는데 빈칸"이 되므로 목록에 합류시킨다.
    // **확정된 담당자도 같은 이유로** 합류시킨다(2026-09-08) — 그 행은 잠긴 채 값을 보여주는
    // 자리라, 옵션이 없으면 확정 사실이 화면에서 사라진다(비활성화된 담당자일수록 그렇다).
    for (const doc of setup?.documents ?? []) {
      const rec = doc.recommendedEducator;
      if (rec && !map.has(rec.contactId)) map.set(rec.contactId, rec);
      const confirmed = doc.confirmedEducator;
      if (confirmed?.contactId && !map.has(confirmed.contactId)) {
        map.set(confirmed.contactId, confirmed);
      }
    }
    return map;
  }, [setup]);

  /**
   * 서버 저장값·기억값·추천값 → 초기 입력 상태. 응답이 갈릴 때만 다시 세운다.
   *
   * ## 우선순위: 저장값 > 공종 기억 > 기본값
   * **저장값이 있으면 그것이 정본**이다 — 설정 완료 후 재진입은 "그 문서에 실제로 확정된
   * 것"을 보여주는 자리라, 공종 관행이 확정된 사실을 덮으면 화면이 거짓말을 한다. 기억은
   * 아직 아무것도 확정되지 않은 칸만 채운다.
   *
   * ## 담당자가 무효면 담당자·채널만 비운다
   * 채널은 담당자 종속이라 함께 비우지만, **리마인더·서명 방식은 그대로 복원**한다 —
   * 담당자와 무관한 축이고, 한 명이 바뀌었다고 현장 관행을 통째로 잊는 것은 과하다(확정 3).
   */
  useEffect(() => {
    if (!setup) return;
    setRows((prev) => {
      const next: Record<string, DocRowState> = {};
      for (const doc of setup.documents) {
        const recommended = doc.recommendedEducator;
        const remembered = doc.rememberedSettings;
        // 기억된 채널은 **이 담당자가 받을 수 있는 것만** 되살린다 — 기억이 "받을 수 없는
        // 채널이 켜진" 상태를 되살리면 화면이 스스로 모순을 그린다(확정 3).
        // 교집합이 비면(예: 이메일만 쓰던 그룹인데 새 담당자는 이메일이 없다) 기억이 없는
        // 행과 **같은 기본값**으로 떨어진다 — 빈 채로 두면 사용자가 만들지도 않은 이유로
        // 발송이 잠기고, 그 행만 다른 규칙으로 비어 있게 된다.
        const rememberedChannels = recommended
          ? (remembered?.channels ?? []).filter((c) => canReceive(recommended, c))
          : [];
        next[doc.id] = prev[doc.id] ?? {
          // 확정 済 문서는 **확정값**이 정본이다(2026-09-08 부분 확정 묶음) — 그 행은 잠긴 채
          // 보여지므로, 추천을 그리면 화면이 확정되지 않은 사람을 확정된 것처럼 말한다.
          contactId: doc.confirmedEducator?.contactId ?? recommended?.contactId ?? '',
          channels: rememberedChannels.length
            ? rememberedChannels
            : defaultChannelsFor(recommended),
          educationStartDate: doc.educationStartDate?.slice(0, 10),
          educationEndDate: doc.educationEndDate?.slice(0, 10),
          // 교육 기간은 기억 대상이 아니다(다음 TBM은 다른 날짜) — 리마인더 시간만 기억한다.
          reminderTime: doc.reminderTime || remembered?.reminderTime || DEFAULT_REMINDER_TIME,
          // 재진입(설정 완료 후) 표시의 정본은 **건별 저장값**이다(서버 응답 doc). 상단 공통값이
          // 아니라 이 값이 그 문서에 실제로 확정된 방식이라, 체크 상태가 그대로 되살아난다.
          // 미확정(null)일 때만 공종 기억이 기본 체크를 세운다.
          offlineSignature:
            doc.signatureMode !== null
              ? doc.signatureMode === 'OFFLINE'
              : remembered?.signatureMode === 'OFFLINE',
          offlineSheetCount: doc.offlineSheetCount ?? 0,
          // 재진입 복원(결정 2026-09-08) — 확정 済 문서도 이 값으로 「내가 직접 진행」을 잠긴
          // 채 보여준다. **기억(rememberedSettings) 대상이 아니다**: 다음 TBM을 누가 진행할지는
          // 공종 관행이 아니라 그때 정하는 것이라, 지난번에 직접 했다고 이번 것을 미리 켜 두면
          // 사용자가 고르지 않은 배타 갈래로 화면이 먼저 넘어간다.
          selfConducted: doc.selfConducted,
        };
      }
      return next;
    });
  }, [setup]);

  const updateRow = (docId: string, patch: Partial<DocRowState>) => {
    setRows((prev) => ({
      ...prev,
      [docId]: { ...prev[docId], ...patch } as DocRowState,
    }));
  };

  /**
   * 유효 서명 방식 = 그 행의 체크 하나. 공통값을 참조하지 않으므로 화면 어디서도 "이 문서가
   * 결국 어떻게 되는지"를 위로 올라가 확인할 필요가 없다. 판정 정본은 여전히 서버다
   * (applySetup가 건별 `signatureMode ?? 공통`으로 저장·발송을 가른다) — 여기서는 표시·payload
   * 구성에만 쓴다.
   */
  const effectiveModeOf = (docId: string): TbmSignatureMode =>
    rows[docId]?.offlineSignature ? 'OFFLINE' : 'ELECTRONIC';

  /**
   * 이 행이 **작성자 직접 진행**인가 — 배타·게이트·payload가 전부 이 한 술어를 본다.
   *
   * 유효 ELECTRONIC일 때만 참이다. 토글은 전자서명 분기에만 서고(아래 렌더), OFFLINE으로
   * 넘어가면 `selfConducted` 상태 값은 남아 있어도 **유효하지 않다** — 채널·교육 기간이 그
   * 방식에서 payload에 실리지 않는 것과 같은 규약이다. 그래야 되돌렸을 때 켜 두었던 것이
   * 되살아나면서도, 켜진 채 오프라인으로 넘긴 행이 계약에 없는 필드를 서버로 보내지 않는다.
   *
   * ⚠️ 오프라인에 이 갈래를 두지 않는 이유(결정 2026-09-08 §3과 대조해 판단): 오프라인은 이미
   * 담당자가 **선택**이라 「입구가 0건」 문제 자체가 없고, `selfConducted`면 서버가
   * `tbmEducatorContactId`를 null로 두므로 켜 봐야 ②「TBM 담당자 확인」에 인쇄될 이름이 생기지
   * 않는다(얻는 것이 0이다). 서명 방식 축은 그대로 살아 있다 — 직접 진행 중에도 「출력물에
   * 직접 서명」은 눌린다.
   */
  const isSelfConductedRow = (docId: string): boolean =>
    Boolean(rows[docId]?.selfConducted) && effectiveModeOf(docId) === 'ELECTRONIC';

  /**
   * 이 문서가 **이미 확정됐나** — 확정은 되돌릴 수 없으므로 그 행은 값을 보여주되 잠근다
   * (결정 2026-09-08 「부분 확정된 분할 묶음」 (가) 채택).
   *
   * 그룹 하나를 지우고 다시 만들면 재생성 문서가 같은 배치를 물려받아 **부분 확정 묶음**이
   * 된다. 종전엔 게이트가 그걸 `ALREADY_CONFIGURED`로 보고 화면이 `disabled={!gateReady}`
   * 한 덩어리로 전부 잠가, 새 문서를 영영 설정할 수 없었다. 잠금 축을 **문서별**로 내린다.
   */
  const isConfirmed = (doc: TbmSetupDocument) => doc.signatureMode !== null;

  /**
   * 이번에 설정할 문서 = 미확정 건. payload·제출 게이트·버튼 라벨이 전부 이 집합만 본다 —
   * 확정된 형제가 제출을 막으면(그 문서 칸이 이 요청의 요구를 못 채운다는 이유로) 부분 확정
   * 묶음이 다시 못 나아간다. 목록에서 **숨기지는 않는다**: 확정된 형제도 그려야 묶음 범위를
   * 오해하지 않는다((나) 기각 근거).
   */
  const pendingDocs = (setup?.documents ?? []).filter((doc) => !isConfirmed(doc));

  const applyMutation = useMutation({
    mutationFn: () => {
      // documents는 항상 싣는다 — 건별 예외·장수가 이 배열로만 서버에 간다(**미확정 건**
      // 정확 1회). 이미 확정된 형제는 싣지 않는다(2026-09-08): 서버가 그것을 400으로 거부하고,
      // 그게 옳다 — 확정은 되돌릴 수 없으므로 다시 보낼 것이 없다.
      const payloadDocuments = pendingDocs.map((doc) => {
        const row = rows[doc.id];
        // 서버 계약(유효 = 건별 `signatureMode` ?? 공통 `dto.signatureMode`)은 그대로 두고,
        // 화면이 공통을 항상 ELECTRONIC으로 실으므로 **체크된 건만** 건별 OFFLINE을 싣는다.
        const override = row?.offlineSignature ? { signatureMode: 'OFFLINE' as TbmSignatureMode } : {};
        if (effectiveModeOf(doc.id) === 'OFFLINE') {
          // OFFLINE 건 — 발송이 없어 채널·기간은 싣지 않지만 **담당자는 싣는다**
          // (결정 2026-09-08). 서버는 이 값을 문서 속성으로 저장하고 토큰은 만들지 않는다.
          // 연락처는 보내지 않는다: 발송이 없으니 쓸 곳이 없고, 실으면 "연락처가 필요한 축"
          // 이라는 인상을 다시 만든다(그 오해가 이 결정의 출발점이었다).
          const offlineContact = candidateById.get(row?.contactId ?? '');
          return {
            reportId: doc.id,
            ...override,
            offlineSheetCount: row?.offlineSheetCount ?? 0,
            ...(offlineContact?.name
              ? {
                  educator: {
                    contactId: offlineContact.contactId,
                    name: offlineContact.name,
                  },
                }
              : {}),
          };
        }
        const contact = candidateById.get(row?.contactId ?? '');
        const period =
          row?.educationStartDate && row?.educationEndDate
            ? {
                startDate: row.educationStartDate,
                endDate: row.educationEndDate,
                reminderTime: row.reminderTime || DEFAULT_REMINDER_TIME,
              }
            : undefined;
        if (isSelfConductedRow(doc.id)) {
          // 직접 진행 — **플래그와 교육 기간뿐이다**(결정 2026-09-08 §2). `educator`를 싣지
          // 않는 것이 이 갈래의 보안 축이다: 화면이 `{name, phone}`을 실을 수 있으면 "본인"을
          // 주장하며 임의 번호로 문자를 보낼 자리가 생긴다. 신원은 서버가 인증된 요청자에서
          // 해소한다. `channels`도 없다 — 보낼 대상이 없다(토큰 미발급, 알림은 작성자 push).
          //
          // 교육 기간은 남는다: 직접 진행이어도 다회차일 수 있고, 리마인더 시각은 그 축이
          // 소유한다(크론이 그 시각에 작성자 push를 보낸다, §5).
          return {
            reportId: doc.id,
            ...override,
            selfConducted: true,
            educationPeriod: period,
          };
        }
        return {
          reportId: doc.id,
          ...override,
          educator: {
            contactId: contact?.contactId,
            name: contact?.name ?? '',
            phone: contact?.phone ?? '',
            email: contact?.email ?? undefined,
          },
          educationPeriod: period,
          // 고른 것을 그대로 싣는다. 빈 채널을 SMS로 메우던 폴백을 두지 않는 이유는 둘이다:
          // ① 그 규칙의 정본은 이미 서버다(`tbm-distribution-setup.service.ts` — 채널이 비면
          // `['sms']`), 여기서 한 번 더 적으면 같은 기본값이 두 곳에서 갈릴 수 있다.
          // ② 이 화면은 "채널은 담당자에 종속"으로 바뀌었다 — 비어 있음이 곧 의미인데 그것을
          // 클라이언트가 덮으면 사용자가 끈 채널로 발송이 나간다. 어차피 유효 ELECTRONIC 건의
          // 빈 채널은 `missingChannel` 게이트가 제출 자체를 막는다.
          channels: row?.channels ?? [],
        };
      });
      return applyTbmDistributionSetup(id!, companyId!, {
        // 공통 기본값은 **항상 전자서명**이다 — 예외는 건별 override로만 표현한다(화면에 공통
        // 선택지가 없으므로 이 값이 사용자 입력으로 갈릴 여지 자체가 없다). documents는 서버가
        // 요구하는 축(이번에 설정할 문서 전부, 정확히 1회씩)을 그대로 싣는다.
        signatureMode: 'ELECTRONIC',
        documents: payloadDocuments,
      });
    },
    onSuccess: async (result) => {
      // ⚠️ 상세로 보내기 **전에** 앵커 캐시를 갱신해야 한다. 상세의 진입 라우팅은
      // `isSetupComplete`(= `tbmSignatureMode != null`)를 캐시된 문서에서 읽으므로, 옛 값이
      // 남아 있으면 상세가 곧바로 이 화면으로 되돌려보내는 왕복이 된다.
      // 키를 앵커 하나로 좁히지 않는다 — `:id`가 앵커가 아닌 형제일 수 있고, 그러면 정작
      // 지금 보고 있던 문서의 캐시가 낡은 채 남는다(묶음 전 건이 함께 확정되므로 전부 낡는다).
      await queryClient.invalidateQueries({ queryKey: ['report'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['siblingReports'] });
      queryClient.invalidateQueries({ queryKey: ['tbmDistributionSetup'] });
      showToast(
        // 발송이 일어났는지는 결과(발송 건수)로 가른다. 발송 0건인 이유는 **두 가지**이고
        // 각각 다음에 할 일이 다르다(2026-09-08): 전 건이 출력물 직접 서명이면 종이로 서명을
        // 받는 것이고, 직접 진행이면 앱에서 교육 화면을 여는 것이다. 종전 한 문장으로 덮으면
        // 직접 진행 확정에도 "출력물에 직접 서명을 받아 보관하세요"가 떠 하지 않을 일을 시킨다.
        result.distributions.length > 0
          ? `교육 진행 담당자 ${result.distributions.length}명에게 링크를 발송했습니다.`
          : pendingDocs.some((doc) => isSelfConductedRow(doc.id))
            ? '설정을 확정했습니다. 교육 시간이 되면 앱 알림을 보내드립니다.'
            : '서명 방식을 확정했습니다. 교육 후 출력물에 직접 서명을 받아 보관하세요.',
        'success',
      );
      // 완료 후에는 **첫 분할 그룹**으로 보낸다. `anchorId`는 "어느 문서에서 설정했나"라
      // 마지막 그룹으로 들어왔으면 마지막 그룹이 선택된 채 끝나는데, 묶음 전 건이 한꺼번에
      // 확정되는 자리라 사용자가 기대하는 시작점은 그룹 순서의 처음이다(사용자 적발 9/8).
      // `reportIds`는 서버가 분할 그룹 순서로 준다(그 계약은 서버 spec이 고정한다).
      //
      // 넘기는 것은 진입점이 아니라 **원점**이다(F-52) — 흐름이 여기서 끝나므로 그 상세의
      // 「←」는 이 설정 화면이 아니라 애초에 출발한 목록·점검 상세로 가야 한다.
      // (이 이동은 `replace`라 설정 화면이 히스토리에 남지도 않는다.)
      navigate(`/reports/${result.reportIds[0] ?? result.anchorId}`, {
        replace: true,
        state: { from: entryOrigin.from, fromLabel: entryOrigin.fromLabel },
      });
    },
    onError: (err) => {
      // 서버가 지목한 사유(`details.blocker` + 그 자리 전용 문구)를 **그대로** 보여준다 —
      // "배포 설정에 실패했습니다" 한 줄로 덮으면 무엇이 막았는지가 사용자에게 닿지 않는다.
      // 문구는 `apiErrorMessage`로 — `extractApiError.message`는 본문에 `error`가 없으면 axios
      // 원문("Request failed with status code …")을 그대로 돌려줘 `||` 폴백이 안 걸린다(리뷰 W3).
      setBlockedBy(
        (extractApiError(err).details?.blocker as TbmSetupBlockerCode | undefined) ?? null,
      );
      setFormError(apiErrorMessage(err) ?? '배포 설정에 실패했습니다.');
      // 게이트가 그 사이 바뀌었을 수 있다(다른 사람이 먼저 설정·상신). 재조회로 화면을 맞춘다.
      queryClient.invalidateQueries({ queryKey: ['tbmDistributionSetup', id, companyId] });
    },
  });

  const gateReady = setup?.gate.ready ?? false;
  const documents = setup?.documents ?? [];

  /**
   * 발송·연락처 요구·버튼 라벨 판정은 유효 ELECTRONIC 건 집합만 본다(W7).
   * 대상은 **이번에 설정할 문서**(pendingDocs)다 — 이미 확정된 형제는 이 요청의 몫이 아니다.
   */
  const electronicDocs = pendingDocs.filter((doc) => effectiveModeOf(doc.id) === 'ELECTRONIC');
  const hasElectronic = electronicDocs.length > 0;

  /**
   * 연락처 있는 담당자 요구는 유효 ELECTRONIC 건에만 걸린다(서버 검증 동형).
   *
   * **직접 진행 건은 두 요구 어느 쪽도 받지 않는다**(결정 2026-09-08). 서버가 그 건의 educator
   * 검증을 통째로 우회하므로(§3) 여기서 요구하면 화면만 더 엄격해지는데, 그 결과는 "고칠 방법이
   * 없는 잠금"이다 — 직접 진행 행에는 고를 담당자 칸도 켤 채널도 없어서 제출 버튼이 영영
   * 열리지 않는다. 본인 연락처(`user.phone`)로도 대신 세우지 않는다: 이 갈래는 토큰을 발급하지
   * 않아 발송 자체가 없고(회귀계약 2), 알림은 작성자 push다(§5) — 연락처의 소비처가 없다.
   */
  const dispatchDocs = electronicDocs.filter((doc) => !isSelfConductedRow(doc.id));
  const missingEducator = dispatchDocs.some((doc) => {
    const contact = candidateById.get(rows[doc.id]?.contactId ?? '');
    return !contact || !contact.phone;
  });
  const missingChannel = dispatchDocs.some(
    (doc) => (rows[doc.id]?.channels?.length ?? 0) === 0,
  );

  const submitDisabled =
    !gateReady ||
    applyMutation.isPending ||
    missingEducator ||
    missingChannel ||
    // 설정할 문서가 하나도 없으면 보낼 것이 없다(전 건 확정). 게이트가 이미 닫는 상태지만,
    // 버튼이 게이트와 **별개 축으로도** 정직하도록 함께 본다.
    pendingDocs.length === 0;

  if (!companyId || isLoading) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-4 text-center">
        <p className="text-sm text-ink-500">불러오는 중...</p>
      </div>
    );
  }

  if (loadError || !setup) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-4 text-center">
        <p className="text-sm text-red-600">
          {apiErrorMessage(loadError) ?? '배포 설정 정보를 불러오지 못했습니다.'}
        </p>
        <div className="mt-6">
          {/* 실패 화면의 유일한 문도 진입점으로 — 헤더와 같은 값이다(F-52). 여기서 목록으로
              떨어뜨리면 「불러오지 못했다」가 곧 「왔던 곳을 잃었다」가 된다.
              문구는 헤더와 같은 「← {라벨}」 꼴로 — 종전의 「…으로」는 라벨이 값이 되는 순간
              조사가 갈린다(「상세으로」). 조사를 고르는 규칙을 여기 들일 이유가 없다. */}
          <Link
            to={backNav.backLink}
            state={{ ...entryOrigin, viaSetupBack: true }}
            className="text-brand-600 hover:text-brand-800 font-medium text-sm"
          >
            ← {backNav.backLabel}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="tbm-distribution-setup">
      <div>
        {/* 「←」는 **진입점**으로 — 상세에서 들어왔으면 상세, 작성 화면·직접 URL이면 목록(F-52). */}
        <Link
          to={backNav.backLink}
          // 「사용자가 상세를 보겠다」 축(F-61) — 상세의 §5 라우팅이 이 문서를 다시 설정으로
          // `replace`하지 않게 한다(형제 칩 `viaSiblingChip`과 같은 사유). 원점도 함께 물려줘
          // 상세의 「←」가 애초의 원점으로 이어진다.
          state={{ ...entryOrigin, viaSetupBack: true }}
          className="text-sm text-ink-500 hover:text-ink-700"
          data-testid="setup-back-link"
        >
          ← {backNav.backLabel}
        </Link>
        <h1 className="mt-2 text-xl font-bold text-ink-900">{flow?.name}</h1>
        {flow?.description && <p className="mt-1 text-sm text-ink-600">{flow.description}</p>}
      </div>

      {/* 묶음 문서 목록 — 어느 문서들이 한 묶음인지, 각각 어디까지 왔는지, 그리고 각 문서로
          가는 문 하나. 형제 탭이 있던 자리를 이 목록이 통째로 흡수한다(단일화). */}
      <Card radius="lg" padding="lg" elevation="sm">
        <h2 className="text-sm font-semibold text-ink-900 mb-2">배포 대상 문서</h2>
        <ul className="space-y-1.5" data-testid="setup-bundle-docs">
          {documents.map((doc) => (
            <li key={doc.id} className="flex items-center gap-2 text-sm">
              <Badge tone={getDocStatusBadgeTone(doc.status)} size="sm" dot>
                {resolveReportStatusLabel({ status: doc.status })}
              </Badge>
              <span className="truncate text-ink-700">
                {doc.splitGroupLabel?.trim() || doc.title}
              </span>
              {/*
                검수 링크(W6 — S33·S35·S37): 작성 완료(DRAFT) 건은 발송 전에 내용을 확인·수정할
                수 있어야 한다 — 검수의 "다음 단계 지시"가 곧 이 화면의 발송이다(결정 2026-09-06
                「묶음 위임 동사·검수 확정」). 목적지는 기존 편집 라우트(new-composed
                ?editReportId=) — 편집 화면의 형제 칩으로 전 건을 순회 검수한다. **위임 여부
                무관**(직접 분할 S37 동일 노출)이고, 게이트가 잠겨 있어도(NOT_ALL_DRAFT 등)
                완료 건의 링크는 동작한다(S35 부분 검수). 편집 가능 판정의 정본은 서버
                `canEditReport` — 불가면 new-composed가 상세로 replace한다(siblingWriteHref와
                같은 근사 규율). templateKey는 앵커에서 얻는다(형제는 같은 양식이다).

                ⚠️ 흐름 레지스트리(`resolvePostSubmitEntryRoute`)로 이 목적지를 뽑을 수는 없다 —
                DRAFT + 설정 미완은 그 함수가 `kind:'setup'`, 즉 **이 화면 자신**을 돌려준다.
                검수는 "내용을 고치러 가는" 별개 축이라 상태별로 갈린다(아래 CREATING과 대비).
              */}
              {doc.status === 'DRAFT' && anchorReport && (
                <Link
                  to={composedEditHref(anchorReport.templateKey, doc.id)}
                  // 원점(`origin`)을 함께 물려준다(리뷰 W6) — 검수 뒤 저장 → 상세 → 설정으로
                  // 되돌아올 때 「←」와 확정 뒤 이동이 애초의 원점(목록·점검 상세)을 잃지 않게.
                  state={{ from: location.pathname, fromLabel: flow?.name, origin: entryOrigin }}
                  className="ml-auto flex-none text-xs font-medium text-brand-600 hover:text-brand-800"
                  data-testid={`setup-review-link-${doc.id}`}
                >
                  내용 확인·수정
                </Link>
              )}
              {/*
                미완 문서로 가는 문(2026-09-07 단일화 2) — 이건 편의 확장이 아니라 **문구-동작
                불일치의 해소**다: 게이트 안내가 "위 문서 목록에서 해당 문서를 눌러 작성을
                이어가세요"라고 말하는데 정작 이 목록의 링크는 DRAFT 전용이었고, 그 역할을 형제
                탭이 대신해 겨우 참이었다.

                목적지를 **문서의 정위치(`/reports/:id`)** 로 두는 것이 곧 "화면이 자체 술어를
                만들지 않는다"이다. 어디로 갈지는 상세가 흐름 레지스트리로 판정해 replace한다
                (`ComposerReportDetail`의 진입 라우팅 = `resolvePostSubmitEntryRoute`): 본인
                CREATING이면 작성 화면으로, 위임본이면 위임 현황이 있는 상세에 머문다. 여기서
                레지스트리를 직접 부를 수는 없다 — 그 판정의 재료인 `templateSnapshot`·`authorId`가
                이 화면의 문서 형상(`TbmSetupDocument`)에도, 목록 응답에도 없다(서버가 응답에서
                templateSnapshot을 뺀다).

                라벨만 화면이 정하고, **위임본은 갈라 적는다**: 그 문서는 내가 이어 쓰는 게 아니라
                남이 쓰는 중이라 "작성 이어가기"는 목적지와 어긋난다. 서버 게이트도 이미 두 축으로
                나눠 말하므로(NOT_ALL_DRAFT "눌러 작성을 이어가세요" / DELEGATION_IN_PROGRESS
                "위임한 담당자의 작성이 끝나면") 목록만 한 라벨로 합치면 같은 화면 안에서 안내 축이
                갈린다. 진행 상태 문구는 덧붙이지 않는다 — 왼쪽 배지가 이미 상태를 말한다.
              */}
              {doc.status === 'CREATING' && (
                <Link
                  to={`/reports/${doc.id}`}
                  state={{ from: location.pathname, fromLabel: flow?.name }}
                  className="ml-auto flex-none text-xs font-medium text-brand-600 hover:text-brand-800"
                  data-testid={`setup-doc-link-${doc.id}`}
                >
                  {doc.delegationState !== null ? '위임 현황' : '작성 이어가기'}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </Card>

      {/* 게이트 미충족 — 사유를 **전부** 보여준다(서버가 전부 수집해 내려준다). */}
      {!gateReady && (
        <div className="space-y-3" data-testid="setup-blockers">
          {setup.gate.blockers.map((blocker) => {
            const notice = blockerNotice(blocker.code, blocker.reportIds.length);
            return (
              <div
                key={blocker.code}
                data-testid={`setup-blocker-${blocker.code}`}
                className="rounded-card border border-amber-200 bg-amber-50 p-3.5"
              >
                <p className="text-sm font-semibold text-amber-900">{notice.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-amber-800">{notice.body}</p>
                {(blocker.code === 'ALREADY_CONFIGURED' ||
                  blocker.code === 'APPROVAL_LOCKED' ||
                  blocker.code === 'POST_SUBMIT_NOT_DECLARED') && (
                  <Link
                    to={`/reports/${setup.anchorId}`}
                    className="mt-2 inline-block text-xs font-medium text-brand-700 hover:text-brand-900"
                  >
                    문서 상세로 이동
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 건별 설정 — 서명 방식은 행의 체크 하나로 정해지고(기본 전자서명), **담당자는 두 방식
          모두**(2026-09-08), 채널·교육 기간은 유효 ELECTRONIC 건에만, 유효 OFFLINE 건은 발송
          대신 서명란 추가 출력 장수를 정한다. */}
      <Card radius="lg" padding="lg" elevation="sm">
        <h2 className="text-sm font-semibold text-ink-900 mb-1">건별 설정</h2>
        <p className="text-xs text-ink-500 mb-2">
          기본은 전자서명입니다 — 지정한 담당자에게 교육 진행 링크가 발송됩니다. 인터넷을 쓰기
          어려운 문서만 「출력물에 직접 서명」을 체크하세요.
        </p>
        {/* 공통 기본값 섹션이 사라져 이 경고도 여기로 옮겼다 — 되돌릴 수 없는 액션의 안내라
            자리를 잃는다고 생략할 수 없다(testid도 유지: 이 문구의 존재가 회귀 축이다).
            그 불가역이 곧 부분 확정 묶음에서 확정된 행을 잠그는 이유이기도 하다(2026-09-08). */}
        <p className="mb-3 text-xs text-orange-600" data-testid="signature-mode-immutable-notice">
          ⚠️ 한 번 확정한 서명 방식은 되돌릴 수 없습니다. 변경하려면 문서를 삭제하고 새로
          작성해야 합니다.
        </p>
        <div className="space-y-3" data-testid="setup-doc-rows">
          {documents.map((doc) => {
            const row = rows[doc.id];
            const effective = effectiveModeOf(doc.id);
            const contact = candidateById.get(row?.contactId ?? '');
            const channels = row?.channels ?? [];
            /** 이 행에서 그 채널을 켤 수 있나 — 담당자가 없으면 어느 채널도 켤 수 없다. */
            const canSend = (channel: Channel) => (contact ? canReceive(contact, channel) : false);
            const label = doc.splitGroupLabel?.trim() || doc.title;
            /**
             * 이 행의 잠금 — 종전의 `!gateReady` 한 덩어리에 **문서별 축**을 더한다
             * (2026-09-08). 확정된 문서는 게이트가 열려 있어도 잠긴다: 서명 방식이 되돌릴 수
             * 없으니 그 행에서 고칠 수 있는 것이 없다. 값은 그대로 보인다 — 숨기면 "묶음이
             * 이것뿐"이라는 오해를 만든다((나) 기각 근거).
             */
            const confirmed = isConfirmed(doc);
            const locked = !gateReady || confirmed;
            /** 이 행이 직접 진행인가 — 술어는 위 `isSelfConductedRow` 하나뿐이다. */
            const selfConducted = isSelfConductedRow(doc.id);
            return (
              <Card
                key={doc.id}
                data-testid={`setup-doc-row-${doc.id}`}
                padding="sm"
                elevation="none"
                className="space-y-2.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-ink-900 truncate">{label}</p>
                  {/* 유효 서명 방식 = 이 체크 하나(미체크 전자서명 / 체크 출력물 직접 서명).
                      aria-label에 그룹 라벨을 붙여 여러 행이 이름으로 구별되게 한다. */}
                  <label
                    className={`flex flex-none items-center gap-1.5 ${
                      locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                    }`}
                  >
                    <input
                      type="checkbox"
                      aria-label={`${label} 출력물에 직접 서명`}
                      data-testid={`setup-offline-signature-${doc.id}`}
                      checked={row?.offlineSignature ?? false}
                      disabled={locked}
                      onChange={(e) => updateRow(doc.id, { offlineSignature: e.target.checked })}
                      className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-ink-700">출력물에 직접 서명</span>
                  </label>
                </div>

                {/*
                  왜 이 행이 잠겼는지 — 게이트 미충족은 위 배너가 이미 말하므로 여기서는
                  **확정 済**만 말한다(같은 화면에서 두 이유가 겹쳐 뜨면 어느 쪽이 내 문제인지
                  갈린다). 값을 숨기지 않고 잠그기로 한 이상, 왜 못 고치는지는 행이 말해야 한다.
                */}
                {confirmed && (
                  <p
                    className="text-xs text-ink-500"
                    data-testid={`setup-doc-confirmed-${doc.id}`}
                  >
                    이미 배포 설정이 확정된 문서입니다. 서명 방식은 되돌릴 수 없어 값을 고칠 수
                    없습니다.
                  </p>
                )}

                {effective === 'OFFLINE' ? (
                  <>
                  {/*
                    오프라인에도 담당자 칸은 남는다(결정 2026-09-08) — 사라지는 것은 채널·링크와
                    교육 기간뿐이다. 종이로 하든 누군가 교육을 진행하고, 그 이름이 회의록
                    ②「TBM 담당자 확인」에 인쇄돼야 손으로 받는 것이 서명 하나로 줄어든다.
                    **필수가 아니다** — 현장에서 유동적으로 정하는 경우가 있어 미지정으로도
                    확정된다(그래서 제출 게이트에도 걸지 않는다).
                  */}
                  <EducatorSelect
                    label={label}
                    candidates={[...candidateById.values()]}
                    recommendedSource={doc.recommendedEducator?.source}
                    value={row?.contactId ?? ''}
                    disabled={locked}
                    // 채널은 이 방식에 존재하지 않는 축이라 담당자를 골라도 세우지 않는다
                    // (payload도 싣지 않는다). 전자서명으로 되돌리면 그때 위 분기가 기본값을
                    // 세운다 — "빈 값 ↔ 선택" 전이 규칙이 그대로 발동한다.
                    onChange={(nextId) => updateRow(doc.id, { contactId: nextId })}
                  >
                    {/* 확정 済 행에는 띄우지 않는다 — "지정하지 않아도 확정할 수 있다"는
                        아직 확정 전인 행에만 참이다(그 행의 안내는 위 확정 문구가 맡는다). */}
                    {!confirmed && (
                      <p className="mt-1 text-xs text-ink-500">
                        {contact
                          ? '링크는 보내지 않습니다. 이 담당자의 소속·직책·성명이 문서의 「TBM 담당자 확인」 칸에 인쇄되고, 서명은 출력물에 직접 받습니다.'
                          : '교육을 진행할 담당자입니다. 지정하지 않아도 확정할 수 있고, 지정하면 이름이 문서에 인쇄됩니다(링크는 보내지 않습니다).'}
                      </p>
                    )}
                  </EducatorSelect>
                  {/*
                    기억된 담당자가 무효라 비워 뒀다는 사실은 방식과 무관하다 — 오프라인에서도
                    말없이 빈칸이면 사용자는 기억이 원래 없었는지 사라졌는지 알 수 없다.
                    (전자서명 분기의 ⓪과 같은 자리·같은 술어. 여기선 채널 축이 없어 그 아래
                    ①②③ 배타 문구가 통째로 성립하지 않으므로 이것만 남는다.)
                  */}
                  {!confirmed && !contact && doc.rememberedSettings?.educatorUnavailable && (
                    <p
                      className="text-xs text-red-600"
                      data-testid={`setup-remembered-educator-gone-${doc.id}`}
                    >
                      이전에 지정한 담당자는 더 이상 지정할 수 없어 비워 두었습니다. 담당자를
                      선택해 주세요.
                    </p>
                  )}
                  <div data-testid={`setup-offline-sheet-${doc.id}`}>
                    <label className="block text-xs text-ink-500 mb-1">
                      참가자 빈 서명지 장수 ({TBM_OFFLINE_SHEET_COUNT_MIN}~
                      {TBM_OFFLINE_SHEET_COUNT_MAX}장)
                    </label>
                    <input
                      type="number"
                      aria-label={`${label} 참가자 빈 서명지 장수`}
                      min={TBM_OFFLINE_SHEET_COUNT_MIN}
                      max={TBM_OFFLINE_SHEET_COUNT_MAX}
                      value={row?.offlineSheetCount ?? 0}
                      disabled={locked}
                      onChange={(e) => {
                        const raw = Number(e.target.value) || 0;
                        const clamped = Math.min(
                          TBM_OFFLINE_SHEET_COUNT_MAX,
                          Math.max(TBM_OFFLINE_SHEET_COUNT_MIN, raw),
                        );
                        updateRow(doc.id, { offlineSheetCount: clamped });
                      }}
                      className="w-24 px-2 py-1.5 text-sm border border-line rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-ink-100"
                    />
                    <p className="text-xs text-ink-500 mt-1">
                      1장당 20명의 서명을 받을 수 있습니다. PDF 마지막에 빈 서명지가 첨부됩니다.
                      {/* 종전 문구의 「담당자에게 보내는 링크 없이」는 담당자와 링크를 한 문장에
                          묶어, 담당자 지정 자체가 없는 것처럼 읽혔다(그 혼선이 이 결정의 출발점
                          이다). 이제 없는 것은 링크뿐이므로 링크만 말한다. */}
                      {' '}이 문서에는 교육 진행 링크를 보내지 않습니다.
                    </p>
                  </div>
                  </>
                ) : (
                  <>
                  {/*
                    「내가 직접 진행」 — 담당자 지정의 **배타 갈래**(결정 2026-09-08). 종전엔 이
                    흐름이 담당자 지정을 강제해 안전관리자가 자기 교육을 여는 입구가 0건이었다.
                    켜면 담당자·발송 채널이 접히고 교육 기간·서명 방식은 그대로 남는다(직접
                    진행이어도 다회차일 수 있고, 서명 방식은 담당자와 무관한 축이다).

                    체크박스가 select보다 **위**에 선다 — 아래 칸을 쓸지 말지를 정하는 스위치라,
                    아래에 두면 사용자가 담당자를 고르고 나서야 안 골라도 된다는 것을 안다.
                  */}
                  <label
                    className={`flex items-center gap-1.5 ${
                      locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                    }`}
                  >
                    <input
                      type="checkbox"
                      aria-label={`${label} 내가 직접 진행`}
                      data-testid={`setup-self-conducted-${doc.id}`}
                      checked={row?.selfConducted ?? false}
                      disabled={locked}
                      onChange={(e) => updateRow(doc.id, { selfConducted: e.target.checked })}
                      className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
                    />
                    <span className="text-xs text-ink-700">내가 직접 진행</span>
                  </label>

                  {selfConducted ? (
                    <div data-testid={`setup-self-conducted-summary-${doc.id}`}>
                      {/*
                        담당자 select가 있던 자리. **이름은 지금 켜는 사람에게만 붙인다** —
                        확정 済 행은 다른 사람이 확정했을 수 있고(이 화면의 문서 형상에 작성자가
                        없다), 그 자리에 지금 보고 있는 사람의 이름을 적으면 화면이 없는 사실을
                        말한다. 모르는 이름을 지어내느니 이름 없이 사실만 적는다.
                      */}
                      <p className="text-sm text-ink-900">
                        내가 직접 진행
                        {!confirmed && user?.name ? ` — ${user.name}` : ''}
                      </p>
                      {/*
                        무엇이 사라졌는지를 말한다 — 채널 체크박스가 통째로 없어진 자리라,
                        말하지 않으면 사용자는 발송이 어딘가에서 일어난다고 믿는다.
                        약속하는 것은 **실제로 하는 것**뿐이다: 토큰·링크는 발급되지 않고
                        (회귀계약 2) 알림은 교육 시각의 작성자 push다(§5).
                      */}
                      {!confirmed && (
                        <p className="mt-1 text-xs text-ink-500">
                          담당자에게 링크를 보내지 않습니다. 앱에서 교육 화면을 열어 직접
                          진행하고, 교육 시간이 되면 앱 알림을 받습니다.
                        </p>
                      )}
                    </div>
                  ) : (
                  <>
                  <EducatorSelect
                    label={label}
                    candidates={[...candidateById.values()]}
                    recommendedSource={doc.recommendedEducator?.source}
                    value={row?.contactId ?? ''}
                    disabled={locked}
                    onChange={(nextId) => {
                      const next = candidateById.get(nextId);
                      // 채널은 담당자에 종속된다 — 다만 **"빈 값 ↔ 선택" 전이에서만** 기본값을
                      // 세운다. 이미 담당자가 있는 행에서 다른 담당자로 바꾸는 것은 사용자가
                      // 만져 둔 채널 선택을 되돌릴 이유가 아니다.
                      //
                      // 다만 **새 담당자가 못 받는 채널은 걷어낸다** — 남겨 두면 발송 실패다.
                      // 종전에는 이메일만 걸렀는데, 연락처 없는 담당자로 바꿀 때 SMS가 남는
                      // 것도 같은 모순이라 `canReceive` 하나로 대칭이 되게 했다(기본값 파생과
                      // 같은 술어를 써야 "고른 결과"와 "바꾼 결과"가 갈리지 않는다).
                      const wasEmpty = !row?.contactId;
                      updateRow(doc.id, {
                        contactId: nextId,
                        channels:
                          wasEmpty || !next
                            ? defaultChannelsFor(next)
                            : channels.filter((c) => canReceive(next, c)),
                      });
                    }}
                  >
                    {/* 확정 済 행에는 띄우지 않는다 — "다른 담당자를 선택하세요"는 고칠 수
                        있는 행에만 참인 안내다(위 배타 분기와 같은 이유). */}
                    {!confirmed && contact && !contact.phone && (
                      <p className="mt-1 text-xs text-red-600">
                        이 담당자는 연락처가 없어 링크를 보낼 수 없습니다. 다른 담당자를
                        선택하거나 현장 담당자 정보에 연락처를 추가하세요.
                      </p>
                    )}
                  </EducatorSelect>

                  <div className="flex items-center gap-3">
                    {/*
                      두 채널의 잠금은 **같은 술어**(`canReceive`)를 쓴다. 자동으로 붙지 않게
                      하는 것만으로는 손으로 켜는 경로가 남아, 보낼 수 없는 채널이 다시 켜진다 —
                      사용자가 적발한 것의 본질이 그것이다. 채널↔필드 대응 규칙이 파생·전환·잠금
                      세 곳에 따로 적히면 어느 것이 정본인지 화면에서 갈린다.

                      담당자를 아직 안 골랐으면 둘 다 잠기되 **툴팁은 달지 않는다** — 그 경우
                      이유는 "이 담당자에게 …이 없다"가 아니라 담당자 미선택이고, 그 안내는
                      아래 줄이 이미 말한다(틀린 이유를 대는 것은 침묵보다 나쁘다).
                    */}
                    <label
                      className={`flex items-center gap-1.5 ${canSend('sms') ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                      title={
                        contact && !canSend('sms') ? '이 담당자에게 연락처 정보가 없습니다' : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        checked={channels.includes('sms')}
                        disabled={locked || !canSend('sms')}
                        onChange={() =>
                          updateRow(doc.id, {
                            channels: channels.includes('sms')
                              ? channels.filter((c) => c !== 'sms')
                              : [...channels, 'sms'],
                          })
                        }
                        className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-ink-700">SMS</span>
                    </label>
                    <label
                      className={`flex items-center gap-1.5 ${canSend('email') ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                      title={
                        contact && !canSend('email')
                          ? '이 담당자에게 이메일 정보가 없습니다'
                          : undefined
                      }
                    >
                      <input
                        type="checkbox"
                        checked={channels.includes('email')}
                        disabled={locked || !canSend('email')}
                        onChange={() =>
                          updateRow(doc.id, {
                            channels: channels.includes('email')
                              ? channels.filter((c) => c !== 'email')
                              : [...channels, 'email'],
                          })
                        }
                        className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 disabled:cursor-not-allowed"
                      />
                      <span className="text-xs text-ink-700">이메일</span>
                    </label>
                    {/*
                      이 행이 발송을 막고 있는 이유는 **한 번에 하나만** 말한다. 네 상태가
                      배타여야 하는 이유는 각각의 출구가 다르기 때문이다:

                      ⓪ 담당자 미선택인데 **기억된 담당자가 무효**(비활성·탈퇴·연락 수단 없음) —
                         비운 것이 우리이므로 그 사실을 알린다(말없이 빈칸이면 사용자는 기억이
                         원래 없었는지 사라졌는지 알 수 없다). 출구는 ①과 같은 select라, ①을
                         **대체**한다 — 둘 다 띄우면 한 행이 같은 말을 두 번 한다.
                         담당자가 채워졌으면(위임 추천이 대신 들어왔거나 사용자가 골랐거나)
                         비운 자리가 없으므로 침묵한다.
                      ① 담당자 없음 — 채널이 빈 것은 담당자가 없어서다. 여기서 "채널을
                         선택하세요"라고 말하면 고칠 수 없는 자리를 가리켜 사용자를 헛돌게 한다.
                         그렇다고 아무 말도 안 하면 발송 버튼이 왜 잠겼는지 화면이 말하지 않는
                         막다른 길이 된다 — 그래서 그 자리에 담당자 쪽 출구를 대신 띄운다.
                      ② 담당자는 있는데 연락처 없음 — 출구가 같은 select라, 안내는 위(담당자
                         칸 아래)에 이미 서 있다. 여기서 또 말하면 한 행이 두 번 경고한다.
                      ③ 담당자·연락처가 다 있는데 채널 0 — 그때에야 출구가 이 체크박스다.

                      ⚠️ **확정 済 행에는 넷 다 띄우지 않는다**(2026-09-08). 넷은 전부 "고치면
                      보낼 수 있다"는 안내인데, 그 행은 고칠 수도 보낼 수도 없다 — 잠긴 이유는
                      위 확정 안내가 이미 말한다. 특히 도입 전 확정 문서는 담당자를 되살릴 값이
                      없어 빈칸인데(토큰조차 없는 경우), 거기에 "담당자를 선택해야 링크를 보낼
                      수 있습니다"를 띄우면 할 수 없는 일을 하라고 시키는 셈이다.
                    */}
                    {confirmed ? null : !contact && doc.rememberedSettings?.educatorUnavailable ? (
                      <span
                        className="text-xs text-red-600"
                        data-testid={`setup-remembered-educator-gone-${doc.id}`}
                      >
                        이전에 지정한 담당자는 더 이상 지정할 수 없어 비워 두었습니다. 담당자를
                        선택해 주세요.
                      </span>
                    ) : !contact ? (
                      <span className="text-xs text-red-600">
                        담당자를 선택해야 링크를 보낼 수 있습니다.
                      </span>
                    ) : contact.phone && channels.length === 0 ? (
                      <span className="text-xs text-red-600">
                        발송 채널을 하나 이상 선택하세요.
                      </span>
                    ) : null}
                  </div>
                  </>
                  )}

                  {/* S1 — 다회차(교육 기간)는 검증된 안전관리전문기관만(정본은 서버).
                      **직접 진행 갈래 밖에 선다** — 담당자 축이 접혀도 이 축은 남는다
                      (결정 2026-09-08 §1: "직접 진행이어도 다회차일 수 있다"). */}
                  {showEducationPeriod && (
                    <EducationPeriodFields
                      startDate={row?.educationStartDate}
                      endDate={row?.educationEndDate}
                      reminderTime={row?.reminderTime ?? DEFAULT_REMINDER_TIME}
                      selfConducted={selfConducted}
                      disabled={locked}
                      onChange={(start, end) =>
                        // 「지정 안 함」이 빈 문자열로 오면 **undefined**로 되돌린다 —
                        // DocRowState의 계약이 "고르기 전까지 undefined"라 빈 문자열을 남기면
                        // 미지정 상태가 두 가지 모양을 갖게 된다.
                        updateRow(doc.id, {
                          educationStartDate: start || undefined,
                          educationEndDate: end || undefined,
                        })
                      }
                      onReminderTimeChange={(time) => updateRow(doc.id, { reminderTime: time })}
                    />
                  )}
                  </>
                )}
              </Card>
            );
          })}
        </div>
      </Card>

      {/* 전 건이 출력물 직접 서명이면 발송이 없다 — 안내·라벨이 발송을 약속하지 않는다(S22). */}
      {!hasElectronic && (
        <Card radius="lg" padding="lg" elevation="sm">
          {/* 없는 것은 **발송**이지 담당자가 아니다(2026-09-08) — 종전 문구는 둘을 한 문장에
              묶어 담당자 지정 자체가 없는 것처럼 읽혔다. 지정한 담당자는 문서에 남는다. */}
          <p className="text-sm text-ink-700" data-testid="offline-no-dispatch-notice">
            전자서명을 쓰는 문서가 없습니다. 교육 진행 링크는 발송되지 않고 서명 방식만
            확정됩니다. 지정한 담당자는 문서에 기록되어 「TBM 담당자 확인」 칸에 인쇄됩니다.
          </p>
        </Card>
      )}

      {formError && (
        <div
          className="rounded-card bg-red-50 p-3"
          data-testid="setup-error"
          data-blocker={blockedBy ?? undefined}
        >
          <p className="text-sm text-red-700">{formError}</p>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          variant="primary"
          disabled={submitDisabled}
          onClick={() => {
            setFormError(null);
            setBlockedBy(null);
            applyMutation.mutate();
          }}
        >
          {/* 발송 대상이 없으면 "발송"이라 적지 않는다 — 일어나지 않는 일을 약속하는 라벨이
              된다. 대상이 0이 되는 길은 **둘**이다(2026-09-08): 전 건 출력물 직접 서명, 그리고
              전 건 직접 진행. 그래서 `hasElectronic`이 아니라 `dispatchDocs`를 본다 — 종전
              술어로는 직접 진행 건만 있는 묶음에서도 "담당자에게 발송"이라 적혔다. */}
          {dispatchDocs.length > 0 ? '담당자에게 발송' : '서명 방식 확정'}
        </Button>
      </div>
    </div>
  );
}
